/**
 * Keep Auto on the top rung where the connection can carry it.
 *
 * hls.js judges the connection by how long each segment takes to
 * arrive, and a segment the server makes on request takes as long as
 * its encode. On a 4K concert a 720p segment took three seconds,
 * nearly all of it before the first byte. hls.js also assumes a
 * smaller rung arrives sooner, and here it is the other way round: the
 * original's segments are copies, ready in a second, while every rung
 * below is an encode of three to five.
 *
 * Two moments go wrong because of it, and both are met the same way:
 * the estimate starts again, from one generous enough to pick the top
 * rung - what hls.js does when a title opens. That also puts back the
 * default wait for the first byte, which hls.js keeps as one figure
 * for every rung. On a connection that can carry the original its
 * first segment arrives fast and Auto stays there; on one that cannot
 * it arrives slowly and Auto steps down from what it measured.
 *
 * - Back on Auto after a pick by hand. Every sample hls.js had was an
 *   encode, so it read a LAN good for 100 Mbps as 3 Mbps and stayed at
 *   720p. It could never climb out: each rung up the ladder doubles
 *   the bitrate, the encoder runs at about twice real time, so the
 *   estimate never justified the next rung.
 *
 * - A seek out of what is loaded. The first segments after a jump
 *   wait two to three seconds while the server catches up, and that
 *   wait counts against every rung. With the buffer empty hls.js wants
 *   a segment within four seconds, so it left the original for 1080p -
 *   a slower encode - gave up on that, fell to 288p and took eighteen
 *   seconds to find its way back. On the top rung that is always met
 *   this way. Below it, only when the speed the data last flowed at is
 *   SEEK_MARGIN times the top rung (see cleanSpeed): a seek on a 1080p
 *   rung of a concert whose original the Wi-Fi could carry fell to
 *   288p and took a minute and a half to climb back, while a viewer on
 *   a phone or a thin link is left to hls.js, which is right there.
 *
 * - The start. Knowing nothing, hls.js assumes half a megabit and
 *   begins at 288p, and on a LAN that showed as a moment of 288p
 *   before the original. Where it began on a stretch nobody had
 *   played, every rung but the original was an encode it gave up on,
 *   and it went round between 1080p and 288p for a minute. So a
 *   computer begins from the estimate it had when it last played -
 *   see rememberEstimate.
 */

// The starting estimate, as a multiple of the top rung's bitrate.
// hls.js only picks a rung whose segment it expects within four
// seconds; at four times the bitrate a six-second segment takes one
// and a half, which leaves room for the wait before the first byte.
export const HEADROOM = 4;

// How much faster than the top rung the data has to have flowed before
// a seek below it aims there. Right after a seek nothing is loaded, and
// hls.js gives up on a segment that takes more than half its own length
// to arrive - so it needs about twice the rung's rate. At the margin
// hls.js itself uses for switching up (1 / 0.7), the first segment of a
// 1080p concert's original was given up on after 7 to 42% of seeks,
// depending on how soon the server began sending it; at twice, 1 to 15%.
export const SEEK_MARGIN = 2;

// The clean speed is the middle of this many recent picture segments,
// each at least this large - a small one arrives in a few milliseconds
// and says little about the connection.
export const CLEAN_SAMPLES = 6;
export const CLEAN_MIN_BYTES = 256 * 1024;

/**
 * How fast the data flowed, from the first byte to the last, over the
 * last few segments of picture.
 *
 * Not what hls.js estimates. Its estimate counts the wait before the
 * first byte, and here that wait is the server encoding: on a concert
 * opened cold, segments the Wi-Fi delivered at 38 to 60 Mbps once they
 * started were preceded by two or three seconds each, and the
 * estimate read 25. The time from first byte to last is the
 * connection alone.
 */
const cleanSpeed = (samples: number[]): number | undefined => {
  if (samples.length === 0) {
    return undefined;
  }
  const sorted = [...samples].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
};

type Qualities = EventTarget & { auto: boolean; selected: unknown };

const isLoaded = (media: HTMLMediaElement, at: number) => {
  const { buffered } = media;
  for (let i = 0; i < buffered.length; i++) {
    if (buffered.start(i) <= at && at < buffered.end(i)) {
      return true;
    }
  }
  return false;
};

export const aimHighOnAuto = (
  hls: any,
  qualities: Qualities,
  note?: (text: string) => void,
) => {
  let pickedByHand = false;
  let media: HTMLMediaElement | null = null;
  const flowed: number[] = [];

  const startAgain = (why: string) => {
    // The highest rung Auto may choose, which on a phone is capped to
    // the screen.
    const top = hls.levels?.[hls.maxAutoLevel];
    if (!top?.bitrate) {
      return;
    }
    const estimate = top.bitrate * HEADROOM;
    hls.bandwidthEstimate = estimate;
    note?.(
      `${why}: estimate starts again at ${(estimate / 1e6).toFixed(1)} Mbps`,
    );
  };

  const onChange = () => {
    if (!qualities.auto && qualities.selected) {
      pickedByHand = true;
    }
  };

  const onAutoChange = (event: Event) => {
    if ((event as CustomEvent).detail !== true || !pickedByHand) {
      return;
    }
    pickedByHand = false;
    startAgain('chose Auto');
  };

  // Each segment of picture, measured from its first byte to its last.
  const onLoaded = (_event: string, data: any) => {
    const frag = data?.frag;
    const stats = frag?.stats;
    if (frag?.type !== 'main' || !stats) {
      return;
    }
    const bytes = stats.loaded;
    const took = (stats.loading?.end ?? 0) - (stats.loading?.first ?? 0);
    if (!(bytes >= CLEAN_MIN_BYTES) || !(took > 0)) {
      return;
    }
    flowed.push((bytes * 8 * 1000) / took);
    if (flowed.length > CLEAN_SAMPLES) {
      flowed.shift();
    }
  };

  // hls.js picks the rung for the new position on a timer its own
  // seeking handler sets, so this, run in the same turn, is in time.
  const onSeeking = () => {
    if (
      !media ||
      !hls.autoLevelEnabled ||
      isLoaded(media, media.currentTime)
    ) {
      return;
    }
    if (hls.currentLevel === hls.maxAutoLevel) {
      startAgain('seek on the top rung');
      return;
    }
    const top = hls.levels?.[hls.maxAutoLevel];
    const clean = cleanSpeed(flowed);
    if (top?.bitrate && clean && clean >= top.bitrate * SEEK_MARGIN) {
      startAgain(
        `seek, and the data last flowed at ${(clean / 1e6).toFixed(1)} Mbps, enough for the top rung`,
      );
    }
  };

  const watch = (next: HTMLMediaElement | null) => {
    media?.removeEventListener('seeking', onSeeking);
    media = next;
    media?.addEventListener('seeking', onSeeking);
  };
  const onAttached = (_event: string, data: any) => watch(data?.media ?? null);
  const onDetaching = () => watch(null);

  qualities.addEventListener('change', onChange);
  qualities.addEventListener('auto-change', onAutoChange);
  hls.on('hlsMediaAttached', onAttached);
  hls.on('hlsMediaDetaching', onDetaching);
  hls.on('hlsFragLoaded', onLoaded);
  // Vidstack hands the instance over before attaching it; this covers
  // one that is already attached.
  watch(hls.media ?? null);
  return () => {
    qualities.removeEventListener('change', onChange);
    qualities.removeEventListener('auto-change', onAutoChange);
    hls.off('hlsMediaAttached', onAttached);
    hls.off('hlsMediaDetaching', onDetaching);
    hls.off('hlsFragLoaded', onLoaded);
    watch(null);
  };
};

/**
 * The estimate this device had when it last played, kept in this
 * browser only.
 *
 * A computer that ended on the original remembers a LAN at hundreds of
 * megabits, so it opens the next title on the original: a copy, there
 * in a second. One that ended on a thin link remembers that, and opens
 * low as before. The figure goes stale when the device moves - a
 * laptop taken from home to a café tries the original first, gives up
 * on it after three or four seconds and steps down, and remembers the
 * café from then on.
 *
 * Phones do not use it: they change networks too often for yesterday's
 * figure to say much about today's.
 */
export const REMEMBERED = 'playbackBandwidth';

export const rememberedEstimate = (): number | undefined => {
  try {
    const value = Number(window.localStorage.getItem(REMEMBERED));
    return Number.isFinite(value) && value > 0 ? value : undefined;
  } catch {
    return undefined;
  }
};

export const rememberEstimate = (hls: any) => {
  // After each segment of picture, when hls.js has just taken its
  // sample from it.
  const onBuffered = (_event: string, data: any) => {
    if (data?.frag?.type !== 'main') {
      return;
    }
    const estimate = hls.bandwidthEstimate;
    if (!Number.isFinite(estimate) || estimate <= 0) {
      return;
    }
    try {
      window.localStorage.setItem(REMEMBERED, String(Math.round(estimate)));
    } catch {
      // Storage refused, as in a private window: the next start is
      // just hls.js's own.
    }
  };
  hls.on('hlsFragBuffered', onBuffered);
  return () => hls.off('hlsFragBuffered', onBuffered);
};
